import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../lib/useApi.ts';
import { useCanWrite } from '../lib/auth.tsx';
import { days, duration, money, shortDate, timeOfDay } from '../lib/format.ts';
import {
  CALL_STATUS_TONE,
  CALL_TYPE_LABEL,
  LEAD_STATUS_TONE,
  STAGE_LABEL,
  STAGE_TONE,
  humanise,
  labelFor,
  type LeadDetail as LeadDetailData,
  type TimelineEntry,
} from '../lib/types.ts';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Skeleton,
  SkeletonText,
  Timeline,
  TimelineDivider,
  TimelineItem,
  type TimelineKind,
} from '../components/ui/index.ts';
import { PlusIcon, SparkIcon } from '../components/icons.tsx';
import { LogCallDialog } from '../components/LogCallDialog.tsx';

interface TimelineResponse {
  entries: TimelineEntry[];
  total: number;
}

export default function LeadDetail() {
  const { leadId } = useParams<{ leadId: string }>();
  const canWrite = useCanWrite();
  const [logging, setLogging] = useState(false);

  const lead = useApi<LeadDetailData>(leadId ? `/leads/${leadId}` : null);
  const timeline = useApi<TimelineResponse>(leadId ? `/leads/${leadId}/timeline?limit=200` : null);

  function refresh() {
    lead.reload();
    timeline.reload();
  }

  if (lead.error) {
    return (
      <EmptyState
        title="Could not load that lead"
        description={lead.error}
        action={
          <Link to="/leads">
            <Button size="sm" variant="secondary">
              Back to leads
            </Button>
          </Link>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <Header data={lead.data} loading={lead.loading} />

      <div className="flex items-center justify-between gap-4">
        <h2 className="text-sm font-medium text-fg">
          History
          {timeline.data && (
            <span className="ml-2 text-xs font-normal text-fg-subtle">
              {timeline.data.total} entries
            </span>
          )}
        </h2>
        {canWrite && (
          <Button
            size="sm"
            variant="primary"
            leading={<PlusIcon size={13} />}
            onClick={() => setLogging(true)}
          >
            Log a call
          </Button>
        )}
      </div>

      <Card>
        {timeline.loading && <SkeletonText lines={8} />}
        {!timeline.loading && timeline.data && timeline.data.entries.length === 0 && (
          <EmptyState
            title="Nothing recorded yet"
            description="Log a call, or wait for the next Fathom sync to bring one in automatically."
            compact
            action={
              canWrite ? (
                <Button size="sm" variant="primary" onClick={() => setLogging(true)}>
                  Log a call
                </Button>
              ) : undefined
            }
          />
        )}
        {!timeline.loading && timeline.data && timeline.data.entries.length > 0 && (
          <Spine entries={timeline.data.entries} />
        )}
      </Card>

      {logging && leadId && (
        <LogCallDialog
          leadId={Number(leadId)}
          onClose={() => setLogging(false)}
          onSaved={() => {
            setLogging(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ header */

function Header({ data, loading }: { data: LeadDetailData | null; loading: boolean }) {
  if (loading || !data) {
    return (
      <Card>
        <Skeleton className="h-6 w-56" />
        <Skeleton className="mt-2 h-3 w-40" />
        <div className="mt-5 flex gap-8">
          {Array.from({ length: 5 }, (_, index) => (
            <div key={index}>
              <Skeleton className="h-3 w-16" />
              <Skeleton className="mt-2 h-4 w-20" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  const { lead, currentStage, money: totals, nextEvent } = data;

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl font-semibold tracking-tighter">{lead.full_name}</h1>
            <Badge tone={LEAD_STATUS_TONE[lead.status] ?? 'neutral'}>
              {humanise(lead.status)}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-fg-muted">
            {lead.practice_name ?? 'Practice unknown'}
            {lead.specialty && <span className="text-fg-subtle"> · {lead.specialty}</span>}
            {(lead.city ?? lead.state) && (
              <span className="text-fg-subtle">
                {' '}
                · {[lead.city, lead.state].filter(Boolean).join(', ')}
              </span>
            )}
          </p>
        </div>
        <Link to="/leads" className="text-xs text-fg-subtle transition hover:text-fg">
          All leads
        </Link>
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-3 lg:grid-cols-5">
        <Field label="Source">
          <span className="capitalize">{lead.source}</span>
          {lead.source_detail && (
            <span className="ml-1 text-fg-subtle">({lead.source_detail})</span>
          )}
        </Field>

        <Field label="Current stage">
          {currentStage ? (
            <Badge tone={STAGE_TONE[currentStage.stage] ?? 'neutral'} dot>
              {labelFor(STAGE_LABEL, currentStage.stage)}
            </Badge>
          ) : (
            <span className="text-fg-faint">—</span>
          )}
        </Field>

        <Field label="Days in stage">
          <span className="numeric">{currentStage ? days(currentStage.days_in_stage) : '—'}</span>
        </Field>

        <Field label="Deal value">
          <span className="numeric">
            {totals.contracted && Number(totals.contracted) > 0 ? money(totals.contracted) : '—'}
          </span>
          {Number(totals.collected) > 0 && (
            <span className="ml-1.5 text-xs text-won">{money(totals.collected)} in</span>
          )}
        </Field>

        <Field label="Next event">
          {nextEvent?.next_event_owned === true ? (
            <span className="text-fg">
              {nextEvent.next_event_desc ?? 'Owned'}
              {nextEvent.next_event_at && (
                <span className="ml-1 text-fg-subtle">{shortDate(nextEvent.next_event_at)}</span>
              )}
            </span>
          ) : (
            <Badge tone="risk" dot>
              Nobody owns it
            </Badge>
          )}
        </Field>
      </dl>

      {lead.dq_reason && (
        <p className="mt-4 border-t border-border-subtle pt-3 text-xs text-fg-subtle">
          Disqualified: <span className="text-lost">{lead.dq_reason}</span>
        </p>
      )}
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs text-fg-subtle">{label}</dt>
      <dd className="mt-1 truncate text-sm text-fg">{children}</dd>
    </div>
  );
}

/* ---------------------------------------------------------------- timeline */

/**
 * Renders the interleaved history. Every entry answers the same three
 * questions in the same places: what happened (title), when (right-aligned
 * timestamp), and what it means (the meta line).
 */
function Spine({ entries }: { entries: TimelineEntry[] }) {
  const items: React.ReactNode[] = [];
  let lastMonth = '';

  for (const entry of entries) {
    const date = new Date(entry.occurred_at);
    const month = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (month !== lastMonth) {
      items.push(<TimelineDivider key={`divider-${month}`} label={month} />);
      lastMonth = month;
    }
    items.push(<Entry key={`${entry.kind}-${entry.id}`} entry={entry} />);
  }

  return <Timeline>{items}</Timeline>;
}

function str(data: Record<string, unknown>, key: string): string | null {
  const value = data[key];
  return typeof value === 'string' ? value : null;
}

function num(data: Record<string, unknown>, key: string): number | null {
  const value = data[key];
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

function Entry({ entry }: { entry: TimelineEntry }) {
  const { kind, data, occurred_at: at } = entry;

  /**
   * Only show a clock time when we actually know one.
   *
   * Rows imported from the sheet carry a date and no time, and the seed
   * anchors them at local noon so the calendar day survives any timezone.
   * Printing "12:00 PM" against those would dress that choice up as data.
   * A call has a real time once it has a duration or a Fathom recording;
   * touches are always recorded at a real moment; stage events, deals and
   * payments are day-level facts.
   */
  const hasClockTime =
    kind === 'touch' ||
    (kind === 'call' && (num(data, 'duration_seconds') !== null || str(data, 'fathom_url') !== null));

  const time = hasClockTime ? timeOfDay(at) : '';
  const stamp = `${shortDate(at)}${time ? `, ${time}` : ''}`;

  if (kind === 'call') {
    const status = str(data, 'status') ?? 'scheduled';
    const type = labelFor(CALL_TYPE_LABEL, str(data, 'call_type'));
    const seconds = num(data, 'duration_seconds');
    const owned = data['next_event_owned'];

    const meta = [
      status === 'ran' ? null : status.replace(/_/g, ' '),
      seconds ? duration(seconds) : null,
      owned === false ? 'no next event owned' : null,
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <TimelineItem
        kind="call"
        title={type}
        timestamp={stamp}
        tone={CALL_STATUS_TONE[status] ?? 'open'}
        meta={meta === '' ? undefined : meta}
      >
        {str(data, 'next_event_desc') && (
          <p className="text-xs text-fg-muted">Next: {str(data, 'next_event_desc')}</p>
        )}
        <Analysis data={data['analysis']} />
      </TimelineItem>
    );
  }

  if (kind === 'stage') {
    const stage = str(data, 'stage') ?? '';
    const note = str(data, 'note');
    return (
      <TimelineItem
        kind="stage"
        title={labelFor(STAGE_LABEL, stage)}
        timestamp={stamp}
        tone={STAGE_TONE[stage] ?? 'accent'}
        meta={note ?? undefined}
      />
    );
  }

  if (kind === 'touch') {
    const channel = str(data, 'channel') ?? 'sms';
    const direction = str(data, 'direction') ?? 'outbound';
    const replied = data['got_reply'] === true;
    const timelineKind: TimelineKind = channel === 'email' ? 'email' : 'touch';

    return (
      <TimelineItem
        kind={timelineKind}
        title={`${direction === 'outbound' ? 'Sent' : 'Received'} ${channel}`}
        timestamp={stamp}
        meta={replied ? 'Replied' : direction === 'outbound' ? 'No reply yet' : undefined}
      >
        {str(data, 'body') && (
          <p className="rounded-md border border-border-subtle bg-surface-inset p-2.5 text-xs leading-relaxed text-fg-muted">
            {str(data, 'body')}
          </p>
        )}
      </TimelineItem>
    );
  }

  if (kind === 'deal') {
    const offer = str(data, 'offer') ?? 'other';
    const status = str(data, 'status') ?? 'proposed';
    const value = str(data, 'contract_value');
    const monthly = str(data, 'monthly_amount');

    const meta = [
      offer.replace(/_/g, ' '),
      status.replace(/_/g, ' '),
      monthly ? `${money(monthly)}/mo` : null,
      data['is_renewal'] === true ? 'renewal' : null,
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <TimelineItem
        kind="deal"
        title={value ? `Deal ${money(value)}` : 'Deal'}
        timestamp={stamp}
        tone={status === 'lost' ? 'lost' : 'accent'}
        meta={meta}
      />
    );
  }

  // payment
  const amount = str(data, 'amount');
  const status = str(data, 'status') ?? 'pending';
  return (
    <TimelineItem
      kind="payment"
      title={`Payment ${amount ? money(amount) : ''}`.trim()}
      timestamp={stamp}
      tone={status === 'paid' ? 'won' : status === 'failed' ? 'lost' : 'stalled'}
      meta={[status, str(data, 'method')].filter(Boolean).join(' · ')}
    />
  );
}

/* ---------------------------------------------------------------- analysis */

interface AnalysisData {
  summary?: string | null;
  stated_bottleneck?: string | null;
  true_bottleneck?: string | null;
  bottleneck_gap?: string | null;
  why_now?: string | null;
  urgency_score?: number | null;
  urgency_reason?: string | null;
  likelihood_score?: number | null;
  likelihood_reason?: string | null;
  affordability?: string | null;
  objections?: { type?: string; quote?: string; severity?: string; resolved?: boolean }[] | null;
  key_quotes?: { quote?: string; why_it_matters?: string }[] | null;
  watch_outs?: string[] | null;
  recommended_open?: string | null;
  coaching_notes?: string | null;
  talk_ratio?: string | number | null;
  rep_question_count?: number | null;
  framework_covered?: number | null;
  model?: string | null;
  prompt_version?: string | null;
}

/**
 * The AI read of a call, shown under the call it belongs to.
 *
 * coaching_notes and talk_ratio are simply absent for the guest, stripped by
 * the API rather than hidden here, so this renders whatever it is given.
 */
function Analysis({ data }: { data: unknown }) {
  const [open, setOpen] = useState(false);
  if (!data || typeof data !== 'object') return null;
  const a = data as AnalysisData;

  const stated = a.stated_bottleneck ?? null;
  const truth = a.true_bottleneck ?? null;
  const gap = a.bottleneck_gap ?? null;

  return (
    <div className="mt-2 rounded-md border border-border-subtle bg-surface-inset p-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge tone="accent">
          <SparkIcon size={10} /> analysis
        </Badge>
        {a.urgency_score !== null && a.urgency_score !== undefined && (
          <Badge tone={a.urgency_score >= 7 ? 'won' : a.urgency_score >= 4 ? 'risk' : 'stalled'}>
            urgency {a.urgency_score}/10
          </Badge>
        )}
        {a.likelihood_score !== null && a.likelihood_score !== undefined && (
          <Badge tone="neutral">likelihood {a.likelihood_score}/10</Badge>
        )}
        {a.affordability && <Badge tone="neutral">{a.affordability.replace(/_/g, ' ')}</Badge>}
        {a.talk_ratio !== null && a.talk_ratio !== undefined && (
          <Badge tone={Number(a.talk_ratio) > 0.45 ? 'risk' : 'won'}>
            talk {(Number(a.talk_ratio) * 100).toFixed(0)}%
          </Badge>
        )}
        {a.framework_covered !== null && a.framework_covered !== undefined && (
          <Badge tone="neutral">framework {a.framework_covered}/10</Badge>
        )}
      </div>

      {a.summary && <p className="mt-2.5 text-xs leading-relaxed text-fg-muted">{a.summary}</p>}

      {(stated || truth) && (
        <dl className="mt-3 space-y-1.5 text-xs">
          {stated && (
            <div className="flex gap-2">
              <dt className="w-24 shrink-0 text-fg-subtle">They said</dt>
              <dd className="text-fg-muted">{stated}</dd>
            </div>
          )}
          {truth && (
            <div className="flex gap-2">
              <dt className="w-24 shrink-0 text-fg-subtle">Actually is</dt>
              <dd className="text-fg-muted">{truth}</dd>
            </div>
          )}
          {gap && (
            <div className="flex gap-2">
              <dt className="w-24 shrink-0 text-fg-subtle">The gap</dt>
              <dd className="text-accent-text">{gap}</dd>
            </div>
          )}
        </dl>
      )}

      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="mt-2.5 text-2xs text-fg-subtle transition hover:text-fg"
      >
        {open ? 'Less' : 'More'}
      </button>

      {open && (
        <div className="mt-2.5 space-y-3 border-t border-border-subtle pt-2.5 text-xs">
          {a.urgency_reason && (
            <p className="text-fg-muted">
              <span className="text-fg-subtle">Urgency: </span>
              {a.urgency_reason}
            </p>
          )}
          {a.likelihood_reason && (
            <p className="text-fg-muted">
              <span className="text-fg-subtle">Likelihood: </span>
              {a.likelihood_reason}
            </p>
          )}

          {(a.key_quotes?.length ?? 0) > 0 && (
            <div>
              <p className="text-fg-subtle">In their words</p>
              <ul className="mt-1 space-y-1.5">
                {a.key_quotes?.slice(0, 4).map((entry, index) => (
                  <li key={index} className="border-l-2 border-border pl-2 text-fg-muted">
                    "{entry.quote}"
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(a.watch_outs?.length ?? 0) > 0 && (
            <div>
              <p className="text-fg-subtle">Watch outs</p>
              <ul className="mt-1 space-y-0.5">
                {a.watch_outs?.map((item, index) => (
                  <li key={index} className="text-risk">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {a.coaching_notes && (
            <div>
              <p className="text-fg-subtle">Coaching</p>
              <p className="mt-1 leading-relaxed text-fg-muted">{a.coaching_notes}</p>
            </div>
          )}

          <p className="text-2xs text-fg-faint">
            {a.model} · {a.prompt_version}
            {a.rep_question_count !== null && a.rep_question_count !== undefined
              ? ` · ${a.rep_question_count} questions asked`
              : ''}
          </p>
        </div>
      )}
    </div>
  );
}
